import React, { type ReactNode, type Ref, type RefObject } from 'react';
import { useIntl } from 'react-intl';
import { MdChatBubbleOutline, MdWeb } from 'react-icons/md';
import { EmptyState, IconButton, TOOLTIP_ALIGN_END, type BuddiesWho } from '@aibuild/ui';
import type { UserAppDraft } from '@api/appBuilder';
import type { ToolExecution } from '@hooks/useChat';
import { usesAgent } from '../../appCapabilities';
import AppFrame, { type AppFramePreview } from '../../appFrame/AppFrame';
import AppWindow, { WindowSurface } from '../../appFrame/AppWindow';
import FullscreenArea from '../../appFrame/FullscreenArea';
import { framePage } from '../../appFrame/framePage';
import type { FullscreenControls } from '../../appFrame/useFullscreen';
import type { AppRunController } from '../../appFrame/useAppRunController';
import { useRunCaptureView } from '../../appFrame/useRunCaptureView';
import { localized } from '../../appRunView';
import AppRunExecutionPanel from '../../runSetup/AppRunExecutionPanel';
import BuildingScreen from './BuildingScreen';
import BuildProgress from './BuildProgress';
import PreviewRunBar from './PreviewRunBar';
import BuildSketch from './sketch/BuildSketch';
import type { BuildStatus } from './buildSteps';
import type { BuildStage } from './useBuildStage';
import { useCanvasOptions } from './useCanvasOptions';
import { CanvasBar, CanvasRoot, CanvasTools, ChatToggleAnchor, Cover, Stage } from './BuilderCanvas.styled';

interface BuilderCanvasProps {
  readonly draft: UserAppDraft;
  /** Null while the draft cannot run: the preview is then inert. */
  readonly controller: AppRunController | null;
  readonly stale: boolean;
  readonly hidden: boolean;
  readonly chatOpen: boolean;
  readonly onToggleChat?: () => void;
  readonly stage: BuildStage;
  readonly brief: string | null;
  readonly onSketchDone: () => void;
  readonly build: BuildStatus;
  readonly steps: readonly ToolExecution[];
  readonly buddy: BuddiesWho;
  readonly onStop: () => void;
  readonly notices: ReactNode;
  readonly coach: ReactNode;
  readonly onFix: (text: string) => void;
  readonly fullscreenRef: RefObject<HTMLDivElement | null>;
  readonly fullscreen: FullscreenControls;
  readonly previewRef?: Ref<AppFramePreview>;
}

const BuilderCanvas: React.FC<BuilderCanvasProps> = ({
  draft,
  controller,
  stale,
  hidden,
  chatOpen,
  onToggleChat,
  stage,
  brief,
  onSketchDone,
  build,
  steps,
  buddy,
  onStop,
  notices,
  coach,
  onFix,
  fullscreenRef,
  fullscreen,
  previewRef,
}) => {
  const intl = useIntl();
  const run = controller?.run ?? null;
  const manifest = controller?.manifest ?? draft.preview;
  const page = draft.preview.presentation?.page ?? null;
  const agent = usesAgent(manifest);
  const { rootRef, captureView } = useRunCaptureView('app_builder', draft.appKey, run);
  const { lastOptions, onOptions } = useCanvasOptions(page);
  const chatLabel = chatOpen
    ? intl.formatMessage({ id: 'apps.builder.chat.collapse', defaultMessage: 'Hide the builder chat' })
    : intl.formatMessage({ id: 'apps.builder.chat.expand', defaultMessage: 'Show the builder chat' });

  let cover: ReactNode = null;
  if (stage === 'sketch' && brief) {
    cover = <BuildSketch appKey={draft.appKey} brief={brief} onDone={onSketchDone} onStop={onStop} />;
  } else if (stage === 'building' || stage === 'revealing') {
    cover = <BuildingScreen status={build} steps={steps} who={buddy} />;
  }

  let app: ReactNode = null;
  if (page) {
    app = (
      <FullscreenArea areaRef={fullscreenRef} fullscreen={fullscreen} title={localized(intl, manifest.name)}>
        <AppWindow>
          {controller && agent && <AppRunExecutionPanel controller={controller} />}
          <WindowSurface>
            <AppFrame
              manifest={manifest}
              page={framePage(manifest, draft.appKey, run?.runId, 'draft')}
              controller={controller}
              title={localized(intl, manifest.name)}
              hidden={hidden}
              captureView={captureView}
              onFixRequest={(fix) => onFix(run ? `${fix}\n\nTest run ${run.runId}` : fix)}
              onOptions={onOptions}
              previewRef={previewRef}
            />
          </WindowSurface>
        </AppWindow>
      </FullscreenArea>
    );
  } else if (!cover) {
    app = (
      <EmptyState
        icon={<MdWeb />}
        title={intl.formatMessage({ id: 'apps.builder.preview.noPage', defaultMessage: 'No page yet' })}
        description={intl.formatMessage({
          id: 'apps.builder.preview.noPageDetail',
          defaultMessage: 'Tell the assistant what the app should do, and it builds the page here.',
        })}
        dataTestId="app-builder__no-page"
      />
    );
  }

  const chatToggle = onToggleChat && (
    <IconButton
      ariaLabel={chatLabel}
      tooltip={chatLabel}
      tooltipAlign={TOOLTIP_ALIGN_END}
      icon={<MdChatBubbleOutline size={18} aria-hidden="true" />}
      size="sm"
      variant="ghost"
      isOpen={chatOpen}
      aria-expanded={chatOpen}
      onClick={onToggleChat}
      dataTestId="app-builder__chat-toggle"
    />
  );

  return (
    <CanvasRoot ref={rootRef} data-testid="app-builder__preview" data-stage={stage}>
      <CanvasBar>
        {stage !== 'sketch' && <BuildProgress status={build} onStop={onStop} />}
        {controller && page && !cover && (
          <PreviewRunBar appKey={draft.appKey} controller={controller} agent={agent} stale={stale} lastOptions={lastOptions} />
        )}
        {chatToggle && (
          <CanvasTools>
            <ChatToggleAnchor>
              {chatToggle}
              {coach}
            </ChatToggleAnchor>
          </CanvasTools>
        )}
      </CanvasBar>
      {!chatToggle && coach}
      {notices}
      <Stage>
        {app}
        {cover && (
          <Cover $over={Boolean(page)} $leaving={stage === 'revealing'} data-testid="app-builder__build-cover">
            {cover}
          </Cover>
        )}
      </Stage>
    </CanvasRoot>
  );
};

export default BuilderCanvas;
